/**
 * V2 My account — edit profile (V1 modals/edit-profile.js parity: name,
 * bio 280, location via Places autocomplete, website/socials, email
 * opt-in, avatar ≤2MB + banner ≤5MB in the avatars bucket) and change
 * password. Arrived at from "Edit profile" on your own journal, or
 * automatically after a password-reset email link (PASSWORD_RECOVERY).
 */
import * as gateway from '../../core/gateway.js';
import { escapeHtml } from '../../../assets/js/utils.js';
import { searchGooglePlaces, placeDetails } from '../log/places.js';
import { fetchBalance, bravoBalanceHtml } from '../bravo/bravo.js';
import { fetchAllUserYoinks } from '../profile/profile-data.js';
import { yoinksCsv, yoinksKml, deliverFile } from './export.js';

const EXPORT_FORMATS = {
  csv: { label: 'CSV', type: 'text/csv;charset=utf-8', build: (rows) => yoinksCsv(rows) },
  kml: { label: 'KML', type: 'application/vnd.google-earth.kml+xml', build: (rows, p) => yoinksKml(rows, { title: `${p.username}’s yoinks` }) },
};

const AVATAR_MAX = 2 * 1024 * 1024;
const BANNER_MAX = 5 * 1024 * 1024;
const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

const PROFILE_SELECT =
  'id,username,full_name,bio,location,profile_lat,profile_lng,profile_place_id,' +
  'website,instagram,twitter,avatar_url,background_image,wants_emails,role';

function imgUrl(path) {
  if (!path) return null;
  return path.startsWith('http') ? path : gateway.getPublicUrl('avatars', path);
}

/** V1 validation: URL gets https:// prefixed; handles lose their @. */
export function normalizeProfileInput({ website, instagram, twitter }) {
  let site = (website || '').trim();
  if (site && !/^https?:\/\//i.test(site)) site = `https://${site}`;
  if (site && !/^https?:\/\/[^\s.]+\.[^\s]+$/i.test(site)) return { error: 'That website doesn’t look like a URL.' };
  return {
    website: site || null,
    instagram: (instagram || '').trim().replace(/^@/, '') || null,
    twitter: (twitter || '').trim().replace(/^@/, '') || null,
  };
}

export function mount(el, ctx) {
  const { store, router } = ctx;
  let destroyed = false;
  let profile = null;
  let saving = false;
  // Pending image changes (files live outside the DOM)
  let avatarFile = null;
  let bannerFile = null;
  let removeAvatar = false;
  let removeBanner = false;
  let pickedPlace = null; // { text, lat, lng, place_id } from autocomplete
  // Read separately from PROFILE_SELECT on purpose: until the bravos
  // migration is applied that column doesn't exist, and folding it into
  // the main select would 400 the whole account page instead of hiding
  // one readout.
  let bravoBalance = null;
  // Export my yoinks: the tap that opens the share sheet must be the tap
  // that asked for it (Safari), so state stays outside the DOM across
  // the fetch and a re-render.
  let exporting = null; // 'csv' | 'kml' while gathering
  let exportStatus = '';
  let exportError = '';
  let exportReady = null; // { format, name, text, type, count } when the sheet refused once

  const basePath = window.YOINK_CONFIG?.basePath || '/adventure';

  // Blob URLs are memoized per File and revoked on cleanup — creating a
  // fresh one on every re-render leaks memory for the life of the tab.
  const objectUrls = new Map();
  function fileUrl(file) {
    if (!objectUrls.has(file)) objectUrls.set(file, URL.createObjectURL(file));
    return objectUrls.get(file);
  }

  function render() {
    const recovery = store.get().passwordRecovery;
    const avatarSrc = avatarFile ? fileUrl(avatarFile)
      : removeAvatar ? null : imgUrl(profile.avatar_url);
    const bannerSrc = bannerFile ? fileUrl(bannerFile)
      : removeBanner ? null : imgUrl(profile.background_image);
    el.innerHTML = `
      <nav class="v2-back" aria-label="Back"><a href="${basePath}/user/${escapeHtml(profile.username)}" data-acct-back>← My journal</a></nav>
      <form class="v2-log" data-account-form novalidate>
        <h2 class="log-heading">My account</h2>

        <div class="v2-field-block">
          <label class="v2-field-name" for="acct-avatar">Avatar</label>
          <div class="log-photo-row">
            ${avatarSrc
              ? `<img class="log-photo-thumb acct-avatar" src="${avatarSrc}" alt="">`
              : `<span class="log-photo-thumb acct-avatar acct-blank">${escapeHtml((profile.full_name || profile.username || '?')[0].toUpperCase())}</span>`}
            <input type="file" id="acct-avatar" name="avatar" accept="image/*">
            ${avatarSrc ? '<button type="button" class="log-change-place" data-remove-avatar>Remove</button>' : ''}
          </div>
        </div>

        <div class="v2-field-block">
          <label class="v2-field-name" for="acct-banner">Banner image</label>
          ${bannerSrc ? `<img class="acct-banner" src="${bannerSrc}" alt="">` : ''}
          <div class="log-photo-row">
            <input type="file" id="acct-banner" name="banner" accept="image/*">
            ${bannerSrc ? '<button type="button" class="log-change-place" data-remove-banner>Remove</button>' : ''}
          </div>
        </div>

        <label class="v2-field">Name
          <input type="text" name="fullName" maxlength="80" value="${escapeHtml(profile.full_name || '')}">
        </label>

        <label class="v2-field">Bio
          <textarea name="bio" rows="3" maxlength="280">${escapeHtml(profile.bio || '')}</textarea>
        </label>
        <div class="log-hint" data-bio-count>${(profile.bio || '').length}/280</div>

        <div class="v2-field-block">
          <label class="v2-field">Location
            <input type="text" name="homeLocation" autocomplete="off" placeholder="Where you’re based"
              value="${escapeHtml(profile.location || '')}">
          </label>
          <div class="log-place-results" data-home-results aria-live="polite"></div>
        </div>

        <label class="v2-field">Website
          <input type="url" name="website" value="${escapeHtml(profile.website || '')}">
        </label>
        <label class="v2-field">Instagram
          <input type="text" name="instagram" value="${escapeHtml(profile.instagram || '')}" placeholder="@handle">
        </label>
        <label class="v2-field">Twitter / X
          <input type="text" name="twitter" value="${escapeHtml(profile.twitter || '')}" placeholder="@handle">
        </label>

        <label class="log-autofollow">
          <input type="checkbox" name="wantsEmails" ${profile.wants_emails !== false ? 'checked' : ''}>
          Email me occasional Yoink news
        </label>

        ${bravoBalance !== null ? `
        <div class="v2-field-block">
          <span class="v2-field-name">Bravos</span>
          ${bravoBalanceHtml(bravoBalance)}
        </div>` : ''}

        <div class="v2-form-error" data-acct-error role="alert"></div>
        <div class="log-status" data-acct-status aria-live="polite"></div>
        <button type="submit" class="v2-btn-leather" ${saving ? 'disabled' : ''}>
          ${saving ? 'Saving…' : 'Save profile'}
        </button>
      </form>

      <form class="v2-log acct-password ${recovery ? 'acct-recovery' : ''}" data-password-form novalidate>
        <h2 class="log-heading">${recovery ? 'Set your new password' : 'Change password'}</h2>
        <label class="v2-field">New password
          <input type="password" name="pw1" autocomplete="new-password">
        </label>
        <label class="v2-field">Type it again
          <input type="password" name="pw2" autocomplete="new-password">
        </label>
        <div class="v2-form-error" data-pw-error role="alert"></div>
        <div class="log-status" data-pw-status aria-live="polite"></div>
        <button type="submit" class="v2-btn-auth v2-btn-signin">Save password</button>
      </form>

      <section class="v2-log acct-export" aria-labelledby="acct-export-heading">
        <h2 class="log-heading" id="acct-export-heading">Export my yoinks</h2>
        <p class="log-hint acct-export-hint">Your whole journal as a file that’s yours to keep.
          <strong>CSV</strong> opens in Numbers, Excel and Google Sheets;
          <strong>KML</strong> puts every yoink on a map in Google Earth or Google My Maps.
          Photos are included as links.</p>
        <div class="acct-export-row">
          ${Object.entries(EXPORT_FORMATS).map(([key, f]) => `
          <button type="button" class="v2-btn-auth v2-btn-signin" data-export="${key}" ${exporting ? 'disabled' : ''}>
            ${exporting === key ? 'Gathering…' : `Export ${f.label}`}
          </button>`).join('')}
        </div>
        ${exportReady ? `
        <button type="button" class="v2-btn-leather acct-export-save" data-export-save>
          Save ${exportReady.name}
        </button>` : ''}
        <div class="v2-form-error" data-export-error role="alert">${escapeHtml(exportError)}</div>
        <div class="log-status" data-export-status aria-live="polite">${escapeHtml(exportStatus)}</div>
      </section>`;
  }

  function fileStamp() {
    return new Date().toISOString().slice(0, 10);
  }

  /**
   * Gather the journal, build the file, hand it over. On iPad the share
   * sheet can refuse when the fetch took too long after the tap
   * (activation expired); the file then waits behind a "Save" button so
   * the next tap shares it with no async work in between.
   */
  async function exportYoinks(key) {
    const format = EXPORT_FORMATS[key];
    if (!format || exporting) return;
    exporting = key;
    exportError = '';
    exportReady = null;
    exportStatus = 'Gathering your yoinks…';
    render();
    try {
      const rows = await fetchAllUserYoinks(profile.id);
      if (destroyed) return;
      if (!rows.length) {
        exporting = null;
        exportStatus = '';
        exportError = 'Nothing to export yet — log your first yoink and come back.';
        render();
        return;
      }
      const file = {
        format: key,
        name: `yoink-${profile.username}-${fileStamp()}.${key}`,
        text: format.build(rows, profile),
        type: format.type,
        count: rows.length,
      };
      exporting = null;
      exportStatus = `${rows.length} yoink${rows.length === 1 ? '' : 's'} ready.`;
      render();
      await handOver(file);
    } catch (err) {
      if (destroyed) return;
      exporting = null;
      exportStatus = '';
      exportError = `Couldn’t gather your yoinks: ${err?.message || 'unknown error'}`;
      render();
    }
  }

  async function handOver(file) {
    let outcome;
    try {
      outcome = await deliverFile(file);
    } catch (err) {
      outcome = 'failed';
      exportError = `Couldn’t hand over the file: ${err?.message || 'unknown error'}`;
    }
    if (destroyed) return;
    const noun = `${file.count} yoink${file.count === 1 ? '' : 's'}`;
    if (outcome === 'shared') {
      exportReady = null;
      exportStatus = `Shared ${noun} as ${file.name}.`;
    } else if (outcome === 'downloaded') {
      exportReady = null;
      exportStatus = `Downloaded ${noun} as ${file.name}. On an iPad, look in Files → Downloads.`;
    } else if (outcome === 'dismissed') {
      // They closed the sheet: keep the file one tap away
      exportReady = file;
      exportStatus = `${noun} ready — tap Save to try again.`;
    } else {
      exportReady = file;
    }
    render();
  }

  function setError(sel, message) {
    const node = el.querySelector(sel);
    if (node) node.textContent = message;
  }

  function pickImage(input, maxBytes, label) {
    const file = input.files?.[0];
    if (!file) return null;
    if (!IMAGE_TYPES.includes(file.type)) {
      setError('[data-acct-error]', `${label} must be a JPG, PNG, GIF, or WebP.`);
      return null;
    }
    if (file.size > maxBytes) {
      setError('[data-acct-error]', `${label} must be under ${Math.round(maxBytes / 1024 / 1024)}MB.`);
      return null;
    }
    setError('[data-acct-error]', '');
    return file;
  }

  async function onHomeSearch(input) {
    const resultsEl = el.querySelector('[data-home-results]');
    if (!resultsEl) return;
    pickedPlace = null;
    const term = input.value.trim();
    if (term.length < 2) {
      resultsEl.innerHTML = '';
      return;
    }
    try {
      const options = await searchGooglePlaces(term);
      if (destroyed) return;
      resultsEl.innerHTML = options.slice(0, 5).map((o, i) => `
        <button type="button" class="log-place-option" data-pick-home="${i}"
          data-place-id="${o.placeId}" data-text="${escapeHtml(o.mainText)}${o.secondaryText ? `, ${escapeHtml(o.secondaryText)}` : ''}">
          <strong>${escapeHtml(o.mainText)}</strong>
          ${o.secondaryText ? `<span>${escapeHtml(o.secondaryText)}</span>` : ''}
        </button>`).join('');
    } catch {
      resultsEl.innerHTML = '';
    }
  }

  async function saveProfile(form) {
    const social = normalizeProfileInput({
      website: form.elements.website.value,
      instagram: form.elements.instagram.value,
      twitter: form.elements.twitter.value,
    });
    if (social.error) return setError('[data-acct-error]', social.error);
    saving = true;
    render();
    const status = (t) => setError('[data-acct-status]', t);
    try {
      // Images first, V1's exact paths ({id}/avatar.ext, {id}/background.ext)
      let avatarPath = profile.avatar_url;
      if (removeAvatar && avatarPath && !avatarPath.startsWith('http')) {
        await gateway.deleteFile('avatars', avatarPath).catch(() => {});
        avatarPath = null;
      }
      if (avatarFile) {
        status('Uploading avatar…');
        const ext = (avatarFile.name.split('.').pop() || 'jpg').toLowerCase();
        const path = `${profile.id}/avatar.${ext}`;
        if (avatarPath && !avatarPath.startsWith('http')) await gateway.deleteFile('avatars', avatarPath).catch(() => {});
        const { error } = await gateway.uploadFile('avatars', path, avatarFile);
        if (error) throw error;
        avatarPath = path;
      }
      let bannerPath = profile.background_image;
      if (removeBanner && bannerPath && !bannerPath.startsWith('http')) {
        await gateway.deleteFile('avatars', bannerPath).catch(() => {});
        bannerPath = null;
      }
      if (bannerFile) {
        status('Uploading banner…');
        const ext = (bannerFile.name.split('.').pop() || 'jpg').toLowerCase();
        const path = `${profile.id}/background.${ext}`;
        if (bannerPath && !bannerPath.startsWith('http')) await gateway.deleteFile('avatars', bannerPath).catch(() => {});
        const { error } = await gateway.uploadFile('avatars', path, bannerFile);
        if (error) throw error;
        bannerPath = path;
      }

      status('Saving…');
      const locationText = form.elements.homeLocation.value.trim();
      const updates = {
        full_name: form.elements.fullName.value.trim() || null,
        bio: form.elements.bio.value.trim() || null,
        location: locationText || null,
        profile_lat: pickedPlace?.lat ?? profile.profile_lat ?? null,
        profile_lng: pickedPlace?.lng ?? profile.profile_lng ?? null,
        profile_place_id: pickedPlace?.place_id ?? profile.profile_place_id ?? null,
        website: social.website,
        instagram: social.instagram,
        twitter: social.twitter,
        avatar_url: avatarPath,
        background_image: bannerPath,
        wants_emails: form.elements.wantsEmails.checked,
      };
      if (!locationText) {
        updates.profile_lat = null;
        updates.profile_lng = null;
        updates.profile_place_id = null;
      }
      const { error } = await gateway.rawUpdate(gateway.TABLES.PROFILES, {
        filters: { id: `eq.${profile.id}` },
        body: updates,
      });
      if (error) throw error;
      // The store update is real regardless — the save happened — but
      // stop before touching this view's DOM if it was unmounted mid-save.
      store.set({ profile: { ...store.get().profile, ...updates } });
      if (destroyed) return;
      profile = { ...profile, ...updates };
      avatarFile = bannerFile = null;
      removeAvatar = removeBanner = false;
      saving = false;
      render();
      setError('[data-acct-status]', 'Saved.');
    } catch (err) {
      if (destroyed) return;
      saving = false;
      render();
      setError('[data-acct-error]', `Couldn’t save: ${err.message}`);
    }
  }

  async function savePassword(form) {
    const pw1 = form.elements.pw1.value;
    const pw2 = form.elements.pw2.value;
    if (pw1.length < 6) return setError('[data-pw-error]', 'At least 6 characters.');
    if (pw1 !== pw2) return setError('[data-pw-error]', 'Those don’t match.');
    setError('[data-pw-error]', '');
    setError('[data-pw-status]', 'Saving…');
    try {
      const { error } = await gateway.updatePassword(pw1);
      if (error) throw error;
      store.set({ passwordRecovery: false });
      if (destroyed) return;
      form.reset();
      setError('[data-pw-status]', 'Password changed.');
    } catch (err) {
      if (destroyed) return;
      setError('[data-pw-status]', '');
      setError('[data-pw-error]', err.message || 'Couldn’t change the password.');
    }
  }

  function onClick(event) {
    const back = event.target.closest('[data-acct-back]');
    if (back) {
      event.preventDefault();
      router.navigate('user', { username: profile.username });
      return;
    }
    const home = event.target.closest('[data-pick-home]');
    if (home) {
      const input = el.querySelector('input[name="homeLocation"]');
      input.value = home.dataset.text;
      el.querySelector('[data-home-results]').innerHTML = '';
      pickedPlace = { text: home.dataset.text, lat: null, lng: null, place_id: home.dataset.placeId };
      placeDetails(home.dataset.placeId).then((d) => {
        if (destroyed || !pickedPlace || pickedPlace.place_id !== home.dataset.placeId) return;
        pickedPlace.lat = d.location?.latitude ?? null;
        pickedPlace.lng = d.location?.longitude ?? null;
      }).catch(() => {});
      return;
    }
    if (event.target.closest('[data-remove-avatar]')) {
      removeAvatar = true;
      avatarFile = null;
      render();
      return;
    }
    if (event.target.closest('[data-remove-banner]')) {
      removeBanner = true;
      bannerFile = null;
      render();
      return;
    }
    const exportBtn = event.target.closest('[data-export]');
    if (exportBtn) {
      exportYoinks(exportBtn.dataset.export);
      return;
    }
    if (event.target.closest('[data-export-save]') && exportReady) {
      exportError = '';
      handOver(exportReady);
    }
  }

  function onInput(event) {
    const name = event.target.name;
    if (name === 'bio') {
      const counter = el.querySelector('[data-bio-count]');
      if (counter) counter.textContent = `${event.target.value.length}/280`;
    } else if (name === 'homeLocation') {
      onHomeSearch(event.target);
    } else if (name === 'avatar') {
      const file = pickImage(event.target, AVATAR_MAX, 'Avatar');
      if (file) {
        avatarFile = file;
        removeAvatar = false;
        persistFieldsThenRender(event.target.form);
      }
    } else if (name === 'banner') {
      const file = pickImage(event.target, BANNER_MAX, 'Banner');
      if (file) {
        bannerFile = file;
        removeBanner = false;
        persistFieldsThenRender(event.target.form);
      }
    }
  }

  // Re-render (for image previews) without losing typed field values
  function persistFieldsThenRender(form) {
    profile = {
      ...profile,
      full_name: form.elements.fullName.value,
      bio: form.elements.bio.value,
      location: form.elements.homeLocation.value,
      website: form.elements.website.value,
      instagram: form.elements.instagram.value,
      twitter: form.elements.twitter.value,
      wants_emails: form.elements.wantsEmails.checked,
    };
    render();
  }

  function onSubmit(event) {
    event.preventDefault();
    if (event.target.matches('[data-account-form]') && !saving) saveProfile(event.target);
    if (event.target.matches('[data-password-form]')) savePassword(event.target);
  }

  async function start() {
    const user = store.get().user;
    if (!user) {
      if (!store.get().authReady) {
        const unsub = store.subscribe((s) => {
          if (!s.authReady || destroyed) return;
          unsub();
          start();
        });
        el.innerHTML = '<div class="v2-loading">Unrolling the map…</div>';
        return;
      }
      el.innerHTML = `
        <nav class="v2-back" aria-label="Back"><a href="${basePath}/" data-route="discover">← Discover</a></nav>
        <div class="v2-empty">Sign in (top right) to manage your account.</div>`;
      return;
    }
    el.innerHTML = '<div class="v2-loading">Unrolling the map…</div>';
    document.title = 'My account | Yoink Adventures v2';
    try {
      const { data, error } = await gateway.rawQuery(gateway.TABLES.PROFILES, {
        select: PROFILE_SELECT,
        filters: { id: `eq.${user.id}` },
        limit: 1,
      });
      if (error) throw error;
      if (destroyed) return;
      profile = data?.[0];
      if (!profile) {
        el.innerHTML = '<div class="v2-error">Couldn’t load your profile.</div>';
        return;
      }
      render();
      fetchBalance(user.id).then((balance) => {
        if (destroyed || balance === null) return;
        bravoBalance = balance;
        render();
      }).catch(() => {});
    } catch {
      if (destroyed) return;
      el.innerHTML = '<div class="v2-error">Couldn’t reach the journal.</div>';
    }
  }

  start();
  el.addEventListener('click', onClick);
  el.addEventListener('input', onInput);
  el.addEventListener('submit', onSubmit);

  return () => {
    destroyed = true;
    for (const url of objectUrls.values()) URL.revokeObjectURL(url);
    objectUrls.clear();
    el.removeEventListener('click', onClick);
    el.removeEventListener('input', onInput);
    el.removeEventListener('submit', onSubmit);
  };
}
